import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Pencil, ArrowRightLeft } from "lucide-react";

export default function EditStaffModal({ staff, currentUser, open, onOpenChange, onSaved }) {
  const isSuperAdmin = currentUser?.role === "super_admin";
  const isHkFo = ["housekeeping_manager", "front_office_manager"].includes(currentUser?.role);
  const canTransfer = !isHkFo; // super_admin + RM + capo_area can transfer

  const [hotels, setHotels] = useState([]);
  const [depts, setDepts] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nome: "", cognome: "", ruolo: "",
    department_id: "", hotel_id: "",
    responsabile_id: "", data_assunzione: "",
    email: "", telefono: "",
  });

  const availableHotels = useMemo(() => {
    if (isSuperAdmin) return hotels;
    return hotels.filter((h) => (currentUser?.hotel_ids || []).includes(h.id));
  }, [hotels, isSuperAdmin, currentUser]);

  const availableDepts = useMemo(() => {
    if (!form.hotel_id) return [];
    return depts.filter((d) => d.hotel_id === form.hotel_id);
  }, [depts, form.hotel_id]);

  useEffect(() => {
    if (!open || !staff) return;
    setForm({
      nome: staff.nome || "",
      cognome: staff.cognome || "",
      ruolo: staff.ruolo || "",
      department_id: staff.department_id || "",
      hotel_id: staff.hotel_id || "",
      responsabile_id: staff.responsabile_id || "",
      data_assunzione: staff.data_assunzione ? staff.data_assunzione.slice(0, 10) : "",
      email: staff.email || "",
      telefono: staff.telefono || "",
    });
    (async () => {
      setLoading(true);
      try {
        const [h, d] = await Promise.all([
          api.get("/hotels"),
          Promise.all(
            (staff.hotel_id ? [staff.hotel_id] : []).map((hid) =>
              api.get(`/departments?hotel_id=${hid}`).then((r) => r.data || []).catch(() => [])
            )
          ),
        ]);
        setHotels(h.data || []);
        setDepts(d.flat());
        // Managers list only for super_admin (needs /users). Others: skip select.
        if (isSuperAdmin) {
          try {
            const u = await api.get("/users?attivo=true");
            setManagers((u.data || []).filter((usr) =>
              ["resident_manager", "capo_area", "housekeeping_manager", "front_office_manager"].includes(usr.role)
            ));
          } catch { setManagers([]); }
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staff?.id]);

  // When hotel changes, reload its departments
  useEffect(() => {
    if (!open || !form.hotel_id) return;
    (async () => {
      try {
        const r = await api.get(`/departments?hotel_id=${form.hotel_id}`);
        // merge with existing depts (avoid dedup issues)
        setDepts((prev) => {
          const others = prev.filter((d) => d.hotel_id !== form.hotel_id);
          return [...others, ...(r.data || [])];
        });
      } catch { /* ignore */ }
    })();
  }, [open, form.hotel_id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nome.trim() || !form.cognome.trim() || !form.ruolo.trim()) {
      toast.error("Nome, cognome e ruolo sono obbligatori"); return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        ruolo: form.ruolo.trim(),
        department_id: form.department_id || null,
        hotel_id: form.hotel_id,
        responsabile_id: form.responsabile_id || null,
        data_assunzione: form.data_assunzione || null,
        email: form.email.trim() || null,
        telefono: form.telefono.trim() || null,
      };
      const oldHotelId = staff.hotel_id;
      const { data } = await api.patch(`/staff/${staff.id}`, payload);
      const oldHotelName = hotels.find((h) => h.id === oldHotelId)?.nome;
      const newHotelName = hotels.find((h) => h.id === data.hotel_id)?.nome;
      if (oldHotelId !== data.hotel_id) {
        toast.success(
          `Dipendente spostato da ${oldHotelName || "hotel precedente"} a ${newHotelName || "nuovo hotel"}. ` +
          "Valutazioni e stelle storiche restano associate al dipendente."
        );
      } else {
        toast.success("Dipendente aggiornato");
      }
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const hotelChanged = form.hotel_id && form.hotel_id !== staff?.hotel_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="edit-staff-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Modifica dipendente
          </DialogTitle>
          <DialogDescription>
            Aggiorna anagrafica, ruolo, hotel, reparto e contatti.
            Le valutazioni e le stelle storiche restano associate al dipendente anche in caso di trasferimento.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Caricamento…
          </div>
        ) : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Nome</Label>
                <Input value={form.nome} onChange={(e) => update("nome", e.target.value)} data-testid="edit-staff-nome" />
              </div>
              <div className="space-y-1">
                <Label>Cognome</Label>
                <Input value={form.cognome} onChange={(e) => update("cognome", e.target.value)} data-testid="edit-staff-cognome" />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Ruolo / mansione</Label>
              <Input
                value={form.ruolo}
                onChange={(e) => update("ruolo", e.target.value)}
                placeholder="Es. Receptionist, Cameriera piani, Cameriere sala, Manutentore"
                data-testid="edit-staff-ruolo"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="flex items-center gap-1.5">
                  Hotel {canTransfer ? null : <span className="text-[10px] text-muted-foreground">(non modificabile)</span>}
                </Label>
                <Select
                  value={form.hotel_id}
                  onValueChange={(v) => update("hotel_id", v)}
                  disabled={!canTransfer}
                >
                  <SelectTrigger data-testid="edit-staff-hotel"><SelectValue placeholder="Seleziona hotel" /></SelectTrigger>
                  <SelectContent>
                    {availableHotels.map((h) => (
                      <SelectItem key={h.id} value={h.id}>{h.nome} · {h.citta}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Reparto</Label>
                <Select value={form.department_id || "none"} onValueChange={(v) => update("department_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="edit-staff-dept"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuno —</SelectItem>
                    {availableDepts.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {hotelChanged && (
              <div className="rounded-lg bg-lavender-50 dark:bg-lavender-950/40 border border-lavender-200 dark:border-lavender-900 px-3 py-2 text-xs text-lavender-800 dark:text-lavender-200 flex items-start gap-2" data-testid="hotel-transfer-notice">
                <ArrowRightLeft className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Trasferimento hotel</strong>: {hotels.find((h) => h.id === staff?.hotel_id)?.nome || "hotel precedente"} → <strong>{hotels.find((h) => h.id === form.hotel_id)?.nome || "nuovo hotel"}</strong>.
                  L'operazione verrà tracciata in <code>staff_move_history</code>. Le valutazioni e le stelle restano associate al dipendente.
                </span>
              </div>
            )}

            {isSuperAdmin && (
              <div className="space-y-1">
                <Label>Responsabile diretto</Label>
                <Select value={form.responsabile_id || "none"} onValueChange={(v) => update("responsabile_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="edit-staff-responsabile"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuno —</SelectItem>
                    {managers
                      .filter((m) => !form.hotel_id || (m.hotel_ids || []).includes(form.hotel_id))
                      .map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.nome} {m.cognome} · {m.role.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <Label>Data assunzione</Label>
              <Input
                type="date"
                value={form.data_assunzione}
                onChange={(e) => update("data_assunzione", e.target.value)}
                className="max-w-xs"
                data-testid="edit-staff-data-assunzione"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Email (opzionale)</Label>
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="edit-staff-email" />
              </div>
              <div className="space-y-1">
                <Label>Telefono (opzionale)</Label>
                <Input value={form.telefono} onChange={(e) => update("telefono", e.target.value)} data-testid="edit-staff-telefono" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving || loading} data-testid="edit-staff-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Salva modifiche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
